import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { MOBILE_TOPBAR_HEIGHT } from "../../contexts/SidebarContext";

// Raw finger-travel is dampened so the indicator feels elastic; the trigger
// threshold is in DAMPENED pixels.
const DAMPING = 0.4;
const MAX_PULL_PX = 96;
const TRIGGER_PULL_PX = 56;

/**
 * Native pull-to-refresh: pulling down while the page (and the touched
 * element's scroll ancestors) are at the top shows a progress indicator and,
 * past the threshold, refetches every ACTIVE react-query query — whatever
 * data the current screen is showing. Listens globally; renders only the
 * indicator. Mounted once in NativeTabLayout, so it never runs on web.
 */
const NativePullToRefresh = () => {
  const queryClient = useQueryClient();
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const pullRef = useRef(0);
  const refreshingRef = useRef(false);
  pullRef.current = pull;
  refreshingRef.current = refreshing;

  useEffect(() => {
    let startY = 0;
    let tracking = false;

    // A pull only counts when nothing under the finger is scrolled — inner
    // scroll containers (e.g. the Super Investors pages) own the gesture
    // until they are back at their top.
    const anyAncestorScrolled = (start: Element | null): boolean => {
      for (let node = start; node && node !== document.documentElement; node = node.parentElement) {
        if (node.scrollTop > 0) return true;
      }
      return false;
    };

    const onTouchStart = (event: TouchEvent) => {
      if (refreshingRef.current) return;
      if (window.scrollY > 0) return;
      // Surfaces that own their touch gestures (e.g. the Radar zoom/pan
      // canvas — a transform container whose scrollTop is always 0, so the
      // ancestor-scroll guard can't see it) opt out with this attribute.
      if ((event.target as Element)?.closest?.("[data-native-gesture-optout]")) return;
      if (anyAncestorScrolled(event.target as Element)) return;
      startY = event.touches[0].clientY;
      tracking = true;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!tracking) return;
      const rawDelta = event.touches[0].clientY - startY;
      if (rawDelta <= 0) {
        if (pullRef.current !== 0) setPull(0);
        return;
      }
      setPull(Math.min(rawDelta * DAMPING, MAX_PULL_PX));
    };

    const onTouchEnd = async () => {
      if (!tracking) return;
      tracking = false;
      const releasedAt = pullRef.current;
      setPull(0);
      if (releasedAt < TRIGGER_PULL_PX) return;

      setRefreshing(true);
      try {
        await queryClient.invalidateQueries({ type: "active" });
      } finally {
        setRefreshing(false);
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [queryClient]);

  const visible = refreshing || pull > 8;
  const armed = pull >= TRIGGER_PULL_PX;

  return (
    <Box
      sx={{
        position: "fixed",
        top: `calc(${MOBILE_TOPBAR_HEIGHT}px + env(safe-area-inset-top, 0px) + 8px)`,
        left: "50%",
        zIndex: 1300,
        transform: `translateX(-50%) translateY(${visible ? pull * 0.5 : -48}px)`,
        opacity: visible ? 1 : 0,
        transition: refreshing ? "none" : "transform 0.15s ease, opacity 0.15s ease",
        pointerEvents: "none",
        borderRadius: "50%",
        p: 1,
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "#242424" : "#f5f5f5",
        border: (theme) =>
          theme.palette.mode === "dark"
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(0,0,0,0.1)",
        display: "flex",
      }}
    >
      {refreshing && <CircularProgress size={20} sx={{ color: "#22c55e" }} />}
      {!refreshing && (
        <CircularProgress
          size={20}
          variant="determinate"
          value={Math.min((pull / TRIGGER_PULL_PX) * 100, 100)}
          sx={{ color: armed ? "#22c55e" : "text.secondary" }}
        />
      )}
    </Box>
  );
};

export default NativePullToRefresh;
